import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { HiOutlineExclamation } from 'react-icons/hi';
import { useOrg } from '../contexts/OrgContext';
import { usersAPI } from '../services/api';

/**
 * Live seat usage for per-user (seat-based) plans.
 *
 * Prefers the server's live counts on /organizations/me (activeUsers, seatsOverLimit);
 * when those aren't returned it counts active users from the users list instead.
 * The cached `usage.users` counter is NOT used — it can drift from reality.
 *
 * Returns { applicable, known, activeUsers, activeTelecallers, seatLimit, overBy, overLimit }
 */
export const useSeatUsage = () => {
  const { organization, plan, paidSeats } = useOrg();
  const isPerUser = Boolean(plan?.isPerUser);
  const serverActiveUsers = typeof organization?.activeUsers === 'number' ? organization.activeUsers : null;

  // Fallback: count from the users list (org-scoped on the server)
  const { data: usersList } = useQuery({
    queryKey: ['seat-usage-users'],
    queryFn: async () => {
      const res = await usersAPI.getAll({ page: 1, limit: 500 });
      return res.data?.data || [];
    },
    enabled: isPerUser,
    staleTime: 60 * 1000,
    retry: false,
  });

  const listActive = Array.isArray(usersList) ? usersList.filter((u) => u.isActive !== false) : null;
  const activeUsers = serverActiveUsers ?? (listActive ? listActive.length : null);
  const activeTelecallers = listActive
    ? listActive.filter((u) => u.role !== 'admin').length
    : (activeUsers != null ? Math.max(activeUsers - 1, 0) : null);

  const seatLimit = (paidSeats || 1) + 1; // paid seats + the free org admin
  const serverFlag = typeof organization?.seatsOverLimit === 'boolean' ? organization.seatsOverLimit : null;
  const overLimit = serverFlag ?? (activeUsers != null ? activeUsers > seatLimit : false);
  const overBy = activeUsers != null ? Math.max(activeUsers - seatLimit, 0) : 0;

  return {
    applicable: isPerUser,
    known: activeUsers != null || serverFlag != null,
    activeUsers,
    activeTelecallers,
    seatLimit,
    overBy,
    overLimit: isPerUser && overLimit,
  };
};

/** Warning banner shown on Plan & Billing and Users when the org has more active users than paid seats. */
const SeatLimitBanner = ({ className = '' }) => {
  const navigate = useNavigate();
  const { paidSeats, billingCycle } = useOrg();
  const { overLimit, activeUsers, activeTelecallers, seatLimit, overBy } = useSeatUsage();

  if (!overLimit) return null;

  const neededSeats = Math.max(paidSeats || 1, activeTelecallers || 0, (activeUsers || 0) - 1);

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 rounded-xl border border-amber-200 bg-amber-50 ${className}`} role="alert">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
          <HiOutlineExclamation className="w-5 h-5 text-amber-600" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-amber-900">Over seat limit</p>
          <p className="text-xs sm:text-sm text-amber-800 mt-0.5">
            {activeUsers != null
              ? <>You have <strong>{activeUsers}</strong> active users but your plan covers <strong>{seatLimit}</strong> ({paidSeats} paid seat{paidSeats === 1 ? '' : 's'} + 1 admin){overBy > 0 ? <> — {overBy} over the limit</> : null}.</>
              : <>You have more active users than your plan's paid seats.</>}
            {' '}Add seats or deactivate users to stay within your plan.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => navigate(`/app/checkout?plan=full-access&cycle=${billingCycle === 'yearly' ? 'yearly' : 'monthly'}&users=${neededSeats}`)}
        className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors whitespace-nowrap self-start sm:self-auto"
      >
        Add seats
      </button>
    </div>
  );
};

export default SeatLimitBanner;
